// ── Date / time formatting ──────────────────────────────────────
import { htmlToText } from "../../../utils/toDisplayString";

export const formatDate = (dateString) => {
  if (!dateString) return "--";
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  let date = new Date(dateString).toLocaleDateString(undefined, options);
  if (date === "Invalid Date") {
    return "--";
  }
  return date;
};

export const formatTimestamp = (dateString) => {
  if (!dateString) return "--";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "--";
  return d.toISOString();
};

// ── Field resolution (defensive — handles multiple API shapes) ────

export const actorLabel = (log) =>
  log?.actorName || log?.actor || log?.actorType || "Unknown";

/** Real actor name only (empty when the API returns just a type + id). */
export const actorDisplayName = (log) => log?.actorName || log?.actor || "";

export const actorTypeLabel = (log) =>
  log?.actorType || log?.actor_type || "UNKNOWN";

export const actionLabel = (log) =>
  log?.action || log?.type || log?.actionType || "Activity";

export const targetLabel = (log) =>
  log?.targetName || log?.target || log?.targetType || "Unknown";

/** Real target name only (empty when the API returns just a type + id). */
export const targetDisplayName = (log) => log?.targetName || log?.target || "";

export const targetTypeLabel = (log) =>
  log?.targetType || log?.target_type || "UNKNOWN";

/** "ExamPin" → "Exam Pin", "PLATFORM_CONFIG" → "Platform Config", "ADMIN" → "Admin". */
export const humanizeToken = (value) => {
  const s = String(value || "").trim();
  if (!s) return "";
  return s
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Short, human-friendly reference for a UUID, e.g. "#01a09077". */
export const shortId = (id) => {
  const s = String(id || "");
  if (!s) return "";
  return `#${s.slice(0, 8)}`;
};

/** Split an ISO timestamp into a short date and time for the table. */
export const formatDateParts = (dateString) => {
  if (!dateString) return { date: "--", time: "" };
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return { date: "--", time: "" };
  return {
    date: d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    time: d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
};

/**
 * Turn the `metadata` object into an ordered list of `[label, value]` pairs.
 * Keys are humanised ("examType" → "Exam Type") and values are stringified so
 * every field the API returned is shown (nothing is dropped).
 */
export const metadataEntries = (log) => {
  const meta = log?.metadata;
  if (!meta || typeof meta !== "object" || Array.isArray(meta)) return [];
  return Object.entries(meta)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => [
      humanizeToken(k),
      Array.isArray(v)
        ? v.join(", ")
        : typeof v === "object"
        ? htmlToText(JSON.stringify(v))
        : htmlToText(String(v)),
    ]);
};

/** Compact one-line summary of the metadata (tooltip fallback). */
export const metadataSummary = (log) => {
  const entries = metadataEntries(log);
  if (entries.length === 0) return null;
  return entries.map(([k, v]) => `${k}: ${v}`).join(" · ");
};

/** Full pretty-print of the metadata object for the detail page. */
export const metadataLabel = (log) => {
  const meta = log?.metadata;
  if (!meta || typeof meta !== "object") return null;
  if (Array.isArray(meta.updatedFields) && meta.updatedFields.length > 0) {
    return `Updated fields: ${meta.updatedFields.join(", ")}`;
  }
  return Object.entries(meta)
    .filter(([, v]) => v != null)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
    .join("\n");
};

// ── Badge / styling helpers ───────────────────────────────────────

export const actorTypeBadgeClass = (actorType) => {
  switch (String(actorType || "").toUpperCase()) {
    case "ADMIN":
      return "bg-purple-100 text-purple-700";
    case "STAFF":
      return "bg-blue-100 text-blue-700";
    case "USER":
      return "bg-emerald-100 text-emerald-700";
    case "SYSTEM":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

export const actionBadgeClass = (action) => {
  const normalized = String(action || "").toLowerCase();
  if (normalized.includes("create") || normalized.includes("add")) {
    return "bg-green-100 text-green-700";
  }
  if (normalized.includes("update") || normalized.includes("edit")) {
    return "bg-amber-100 text-amber-700";
  }
  if (normalized.includes("delete") || normalized.includes("remove")) {
    return "bg-red-100 text-red-700";
  }
  if (normalized.includes("upload") || normalized.includes("import")) {
    return "bg-sky-100 text-sky-700";
  }
  if (
    normalized.includes("logged") ||
    normalized.includes("log in") ||
    normalized.includes("login")
  ) {
    return "bg-indigo-100 text-indigo-700";
  }
  if (normalized.includes("logout") || normalized.includes("log out")) {
    return "bg-gray-100 text-gray-700";
  }
  return "bg-slate-100 text-slate-600";
};

export const targetTypeBadgeClass = (targetType) => {
  switch (String(targetType || "").toUpperCase()) {
    case "STAFFMEMBER":
    case "STAFF":
      return "bg-blue-100 text-blue-700";
    case "USER":
    case "STUDENT":
      return "bg-emerald-100 text-emerald-700";
    case "COURSE":
      return "bg-violet-100 text-violet-700";
    case "CONTENT":
      return "bg-orange-100 text-orange-700";
    case "RESOURCEPERSON":
      return "bg-fuchsia-100 text-fuchsia-700";
    case "INVOICE":
      return "bg-yellow-100 text-yellow-700";
    case "WITHDRAWAL":
      return "bg-pink-100 text-pink-700";
    case "NEWSARTICLE":
      return "bg-violet-100 text-violet-700";
    case "NEWSCATEGORY":
      return "bg-amber-100 text-amber-700";
    case "ADMIN":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-slate-100 text-slate-600";
  }
};

// ── Common filter options ─────────────────────────────────────────

export const ACTOR_TYPE_OPTIONS = [
  { value: "", label: "All Actor Types" },
  { value: "ADMIN", label: "Admin" },
  { value: "STAFF", label: "Staff" },
  { value: "RESOURCE_PERSON", label: "Resource Person" },
  { value: "LEARNER", label: "Learner" },
  { value: "SYSTEM", label: "System" },
];

export const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
];